import { useCallback } from 'react';

import errorLogger, { BUGSNAG_STATUSES } from '@picsart/growth-rc/services/errorLogger';

import useMemoSelector from 'hooks/useMemoSelector';
import useSubscribe from 'hooks/useSubscribe';

import { getCreditsData } from 'store/batch/selectors';
import isUserAuthorized from 'utils/common/isUserAuthorized';

import { getWebhookUsageAmount } from 'services/webhook/creditsService';

const useWebhookCredits = () => {
  const { checkSubscriptionLimits, subscribe } = useSubscribe();
  const creditsData = useMemoSelector(getCreditsData);

  return useCallback(
    async (flowId: string, itemCount: number): Promise<boolean> => {
      if (itemCount <= 0) return true;

      if (!isUserAuthorized()) {
        await subscribe();
        return false;
      }

      let perItemCost = 0;
      try {
        perItemCost = await getWebhookUsageAmount(flowId);
      } catch (error) {
        errorLogger.trigger(`Webhook credits calculation failed: ${error}`, BUGSNAG_STATUSES.ERROR);
        return true;
      }

      if (perItemCost <= 0) return true;

      const totalCost = perItemCost * itemCount;
      const cleared = await checkSubscriptionLimits(creditsData, totalCost);
      return Boolean(cleared);
    },
    [checkSubscriptionLimits, creditsData, subscribe],
  );
};

export default useWebhookCredits;
